/**
 * Compact legend pinned to the map itself.
 *
 * The sidebar already carries a full legend, but on a phone the sheet is
 * collapsed most of the time and the map is all you can see — so the colours
 * would otherwise be unlabelled at exactly the moment you are reading them.
 * Deliberately small and low-contrast: it should be readable when looked at and
 * ignorable when not.
 */

import { useStore } from '../state/store';
import {
  RASTER_LABELS,
  RASTER_SUBTITLES,
  hazardSwatches,
  rasterRampCss,
  rasterScaleLabels,
} from './rasterLegend';

export function MapLegend() {
  const raster = useStore((s) => s.layers.raster);
  const crossSectionOpen = useStore((s) => s.crossSection !== null);
  const watermarkAltitude = useStore((s) => s.layers.watermarkAltitude);
  const hikeFlyWind = useStore((s) => s.layers.hikeFlyWind);
  const hikeFlyReady = useStore((s) => s.hikeFlyReady);

  // The cross-section panel occupies the bottom of the map and carries its own
  // key; two legends stacked there is clutter.
  if (raster === 'none' || crossSectionOpen) return null;

  const labels = rasterScaleLabels(raster);
  // The watermark's whole meaning is the altitude it is drawn at, so the
  // number belongs on the map beside the colours rather than only in the
  // sidebar, which on a phone is usually collapsed.
  const subtitle =
    raster === 'watermark'
      ? `below ${Math.round(watermarkAltitude)} m`
      : raster === 'hikefly'
        ? hikeFlyReady
          ? hikeFlyWind
            ? 'glide needed · headwind and aspect included'
            : RASTER_SUBTITLES.hikefly
          : 'computing…'
        : RASTER_SUBTITLES[raster];

  return (
    <div className="map-legend" aria-hidden="true">
      <div className="map-legend-title">
        {RASTER_LABELS[raster]}
        {subtitle && <span className="map-legend-sub">{subtitle}</span>}
      </div>

      {raster === 'hazard' ? (
        <ul className="map-legend-classes">
          {hazardSwatches().map(({ label, colour }) => (
            <li key={label}>
              <i style={{ background: colour }} />
              {label}
            </li>
          ))}
        </ul>
      ) : (
        <>
          <div className="map-legend-ramp" style={{ background: rasterRampCss(raster) }} />
          {labels && (
            <div className="map-legend-ticks">
              <span>{labels[0]}</span>
              <span>{labels[1]}</span>
              <span>{labels[2]}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
